import { useEffect, useState } from "react";
import { IoClose, IoRefreshOutline, IoWarningOutline } from "react-icons/io5";
import api from "@/api";

/**
 * Bottom sheet showing every expiry lot for a given (store_id, barcode).
 * Opens when the user taps the "N lotti" pill on an aggregated product card.
 * Read-only: quantities are edited on the individual product detail page.
 */
export const LotsDetailSheet = ({ open, storeId, barcode, productName, onClose }) => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !storeId || !barcode) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/products/lots`, { params: { store_id: storeId, barcode } });
        if (!cancelled) setLots(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Errore nel caricamento dei lotti");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, storeId, barcode]);

  if (!open) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayMs = 24 * 60 * 60 * 1000;

  const totalQty = lots.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = String(iso).split("-");
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).split("-").map(Number);
    if (!y || !m || !d) return null;
    const target = new Date(y, m - 1, d);
    target.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - today.getTime()) / dayMs);
  };

  const dotColor = (dLeft) => {
    if (dLeft === null) return "#9ca3af";
    if (dLeft <= 2) return "#dc2626"; // red
    if (dLeft <= 7) return "#f59e0b"; // amber
    if (dLeft <= 30) return "#eab308"; // yellow
    return "#16a34a"; // green
  };

  return (
    <div className="tf-sheet-backdrop" data-testid="lots-detail-sheet" onClick={onClose}>
      <div className="tf-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="tf-between" style={{ marginBottom: 8 }}>
          <div className="tf-h2" style={{ margin: 0, fontSize: 18 }}>
            {productName || "Lotti"}
          </div>
          <button
            type="button"
            data-testid="lots-detail-close"
            className="tf-close-btn"
            onClick={onClose}
            aria-label="Chiudi"
          >
            <IoClose size={20} />
          </button>
        </div>
        <div className="tf-small" style={{ color: "var(--tf-muted)", marginBottom: 12 }}>
          {loading ? "Carico lotti…" : `${lots.length} lott${lots.length === 1 ? "o" : "i"} · Totale ${totalQty} pz`}
        </div>

        {error && (
          <div
            className="tf-small"
            style={{ color: "#dc2626", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}
          >
            <IoWarningOutline size={16} />
            {error}
          </div>
        )}

        {loading && !lots.length ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <IoRefreshOutline size={22} className="tf-spin" />
          </div>
        ) : (
          <div data-testid="lots-detail-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {lots.map((l) => {
              const dLeft = daysLeft(l.expiry_date);
              const color = dotColor(dLeft);
              return (
                <div
                  key={l.id}
                  data-testid={`lot-row-${l.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    borderRadius: 12,
                    background: "rgba(0,0,0,0.03)",
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span
                      aria-hidden
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: color,
                        flexShrink: 0,
                        boxShadow: `0 0 0 3px ${color}22`,
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{fmtDate(l.expiry_date)}</div>
                      {dLeft !== null && (
                        <div className="tf-small" style={{ color: "var(--tf-muted)" }}>
                          {dLeft < 0
                            ? `Scaduto da ${Math.abs(dLeft)}g`
                            : dLeft === 0
                            ? "Scade oggi"
                            : dLeft === 1
                            ? "Scade domani"
                            : `Fra ${dLeft} giorni`}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{l.quantity} pz</div>
                </div>
              );
            })}
            {!loading && lots.length === 0 && !error && (
              <div className="tf-small" style={{ color: "var(--tf-muted)", padding: 12, textAlign: "center" }}>
                Nessun lotto trovato.
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          data-testid="lots-detail-done"
          className="tf-btn-ghost"
          onClick={onClose}
          style={{ marginTop: 12 }}
        >
          Chiudi
        </button>
      </div>
    </div>
  );
};
